import { Transaction, VehicleExpense, Vehicle } from '@/lib/types';
import { getFinancialYearStart, getFinancialYearEnd, stripBasiqPrefix } from '@/lib/format';

export interface TaxTip {
  id: string;
  title: string;
  description: string;
  potentialSaving?: string;
  action?: string;
  category: 'deduction' | 'offset' | 'strategy';
}

export interface TaxSummary {
  fyLabel: string;
  totalIncome: number;
  totalDeductions: number;
  deductionRate: number; // percentage of income claimed
  tips: TaxTip[];
  missedCategories: string[];
}

/** Common Australian deductible expense categories */
const DEDUCTIBLE_CATEGORIES = [
  'Work from Home',
  'Education',
  'Donations',
  'Insurance',
  'Subscriptions',
  'Phone & Internet',
  'Fuel',
  'Transport',
  'Home Maintenance',
];

/** Typical AU deductible keywords in descriptions */
const DEDUCTIBLE_KEYWORDS = [
  'office', 'laptop', 'computer', 'desk', 'chair', 'stationery',
  'uniform', 'laundry', 'union', 'professional', 'course', 'training',
  'accounting', 'tax agent', 'depreciation', 'tools', 'software',
];

export function analyseTaxDeductions(
  transactions: Transaction[],
  vehicles: Vehicle[],
  vehicleExpenses: VehicleExpense[],
): TaxSummary {
  const now = new Date();
  const fyStart = getFinancialYearStart(now);
  const fyEnd = getFinancialYearEnd(now);
  const fyLabel = `${fyStart.getFullYear()}-${(fyStart.getFullYear() + 1).toString().slice(-2)}`;

  const fyTxns = transactions.filter(t => {
    const d = new Date(t.date);
    return d >= fyStart && d <= fyEnd;
  });

  const totalIncome = fyTxns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const totalDeductions = fyTxns.filter(t => t.type === 'expense' && t.taxDeductible).reduce((s, t) => s + t.amount, 0);
  const deductionRate = totalIncome > 0 ? (totalDeductions / totalIncome) * 100 : 0;

  // Find categories with expenses NOT marked as deductible that could be
  const expensesByCategory = new Map<string, { total: number; deductible: number }>();
  for (const t of fyTxns.filter(t => t.type === 'expense')) {
    const cat = t.category;
    const existing = expensesByCategory.get(cat) || { total: 0, deductible: 0 };
    existing.total += t.amount;
    if (t.taxDeductible) existing.deductible += t.amount;
    expensesByCategory.set(cat, existing);
  }

  // Check for potentially deductible expenses not yet tagged
  const missedCategories: string[] = [];
  for (const cat of DEDUCTIBLE_CATEGORIES) {
    const data = expensesByCategory.get(cat);
    if (data && data.deductible === 0 && data.total > 0) {
      missedCategories.push(cat);
    }
  }

  // Check for keyword matches in non-deductible expenses
  const nonDeductibleExpenses = fyTxns.filter(t => t.type === 'expense' && !t.taxDeductible);
  const potentiallyMissed = nonDeductibleExpenses.filter(t =>
    DEDUCTIBLE_KEYWORDS.some(kw => stripBasiqPrefix(t.description).toLowerCase().includes(kw))
  );

  const tips: TaxTip[] = [];

  // Tip: Low deduction rate
  if (totalIncome > 0 && deductionRate < 5) {
    tips.push({
      id: 'low_deductions',
      title: 'Low Deduction Rate',
      description: `You've only claimed ${deductionRate.toFixed(1)}% of income as deductions. The average Australian claims ~$3,000. Review your expenses for missed deductions.`,
      category: 'deduction',
      action: 'Review transactions and toggle "Tax Deductible" on eligible expenses.',
    });
  }

  // Tip: Missed categories
  if (missedCategories.length > 0) {
    tips.push({
      id: 'missed_categories',
      title: 'Potentially Missed Deductions',
      description: `You have expenses in ${missedCategories.join(', ')} that aren't marked as tax deductible. Some of these may qualify.`,
      category: 'deduction',
      action: 'Check these categories in Transactions and mark eligible items as deductible.',
    });
  }

  // Tip: Keyword-matched items
  if (potentiallyMissed.length > 0) {
    const total = potentiallyMissed.reduce((s, t) => s + t.amount, 0);
    tips.push({
      id: 'keyword_matches',
      title: `${potentiallyMissed.length} Expenses May Be Deductible`,
      description: `We found ${potentiallyMissed.length} expense(s) totalling $${total.toFixed(0)} with work-related keywords (e.g. "${stripBasiqPrefix(potentiallyMissed[0].description)}") not marked as deductible.`,
      potentialSaving: `Up to $${(total * 0.325).toFixed(0)} back at 32.5% marginal rate`,
      category: 'deduction',
      action: 'Review and toggle deductible on qualifying items.',
    });
  }

  // Tip: WFH deduction
  const hasWFH = fyTxns.some(t => stripBasiqPrefix(t.description).toLowerCase().includes('work from home') || t.category === 'Work from Home');
  if (!hasWFH && totalIncome > 0) {
    tips.push({
      id: 'wfh_deduction',
      title: 'Claim Work From Home Expenses',
      description: 'If you work from home, you can claim 67c/hour under the revised fixed rate method (from 1 July 2023). Track your hours to maximise this.',
      potentialSaving: 'Up to $1,340/year (2,000 hours × $0.67)',
      category: 'deduction',
    });
  }

  // Tip: Vehicle expenses
  if (vehicles.length > 0) {
    const fyVehicleExp = vehicleExpenses.filter(ve => {
      const d = new Date(ve.date);
      return d >= fyStart && d <= fyEnd;
    });
    const totalVehicle = fyVehicleExp.reduce((s, ve) => s + ve.amount, 0);
    const avgWorkUse = vehicles.reduce((s, v) => s + v.workUsePercent, 0) / vehicles.length;
    if (totalVehicle > 0 && avgWorkUse > 0) {
      const claimable = totalVehicle * (avgWorkUse / 100);
      tips.push({
        id: 'vehicle_deduction',
        title: 'Vehicle Work-Use Deduction',
        description: `You have $${totalVehicle.toFixed(0)} in vehicle expenses with ${avgWorkUse.toFixed(0)}% work use. Ensure you're claiming the work portion.`,
        potentialSaving: `$${claimable.toFixed(0)} deductible`,
        category: 'deduction',
      });
    }
  }

  // Tip: Super contributions
  const superContributions = fyTxns
    .filter(t => t.type === 'income' && t.superContribution && t.superContribution > 0)
    .reduce((s, t) => s + (t.superContribution ?? 0), 0);
  if (superContributions > 0 && superContributions < 27500) {
    tips.push({
      id: 'super_topup',
      title: 'Top Up Your Super',
      description: `You've contributed $${superContributions.toFixed(0)} to super this FY. The concessional cap is $30,000. Additional contributions are tax-deductible.`,
      potentialSaving: `Up to $${((30000 - superContributions) * 0.15).toFixed(0)} tax saving`,
      category: 'strategy',
    });
  }

  // Tip: Donations
  const donations = fyTxns.filter(t => t.category === 'Donations' && t.taxDeductible);
  if (donations.length === 0) {
    tips.push({
      id: 'donations',
      title: 'Charitable Donations Are Deductible',
      description: 'Donations of $2+ to registered DGR charities are fully tax deductible. If you donate, make sure to tag them.',
      category: 'offset',
    });
  }

  // Tip: Private health insurance
  if (totalIncome > 0 && totalIncome < 93000) {
    tips.push({
      id: 'phi_rebate',
      title: 'Private Health Insurance Rebate',
      description: 'If you have PHI, you may be eligible for the government rebate (up to 24.6%). Check you\'re receiving it on your policy.',
      category: 'offset',
    });
  }

  return { fyLabel, totalIncome, totalDeductions, deductionRate, tips, missedCategories };
}
